import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  RefreshControl,
  ScrollView,
} from 'react-native';
import {
  Text,
  Card,
  Button,
  useTheme,
  ActivityIndicator,
  Avatar,
  IconButton,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  Heart,
  Coffee,
  Users,
  BookOpen,
  CheckCircle2,
} from 'lucide-react-native';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchUserRecipes } from '../store/slices/recipeSlice';
import { unfollowCreator, fetchCreators } from '../store/slices/creatorSlice';
import {
  Recipe,
  useRecipePrefsItem,
  useRecipePrefsStore,
} from '@coffeeplexity/shared';
import { RecipeCard } from '@coffeeplexity/ui';
import {
  useRecipeInteractions,
  useBulkRecipeInteractions,
} from '@coffeeplexity/api-client';
import { logger } from '../utils/logger';

type FavoritesScreenNavigationProp = {
  navigate: (screen: string, params?: any) => void;
  getParent: () => any;
};

// Memoized CreatorCard wrapper
const CreatorCardItem = memo(
  ({
    creator,
    onPress,
    onUnfollow,
    theme,
  }: {
    creator: any;
    onPress: () => void;
    onUnfollow: () => void;
    theme: any;
  }) => (
    <Card
      style={[styles.creatorCard, { backgroundColor: theme.colors.surface }]}
      onPress={onPress}
    >
      <View style={styles.removeButtonContainer}>
        <IconButton
          icon='close'
          size={20}
          onPress={onUnfollow}
          style={styles.removeButton}
          iconColor={theme.colors.error}
        />
      </View>
      <Card.Content>
        <View style={styles.creatorHeader}>
          <View style={styles.avatarContainer}>
            <Avatar.Image
              size={60}
              source={{
                uri:
                  creator.profileImage ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(creator.displayName)}&size=120`,
              }}
              style={styles.avatar}
            />
            {creator.isVerified && (
              <View style={styles.verifiedBadge}>
                <CheckCircle2 size={16} color={theme.colors.primary} />
              </View>
            )}
          </View>

          <View style={styles.creatorInfo}>
            <Text
              variant='titleMedium'
              style={{ color: theme.colors.onSurface, marginBottom: 4 }}
              numberOfLines={1}
            >
              {creator.displayName}
            </Text>
            <Text
              variant='bodySmall'
              style={{
                color: theme.colors.onSurfaceVariant,
                marginBottom: 8,
              }}
            >
              @{creator.username}
            </Text>

            {creator.bio && (
              <Text
                variant='bodySmall'
                style={{
                  color: theme.colors.onSurfaceVariant,
                  marginBottom: 12,
                }}
                numberOfLines={2}
              >
                {creator.bio}
              </Text>
            )}

            <View style={styles.creatorMeta}>
              <View style={styles.metaItem}>
                <Users size={16} color={theme.colors.primary} />
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.primary, marginLeft: 4 }}
                >
                  {creator.followerCount >= 1000000
                    ? `${(creator.followerCount / 1000000).toFixed(1)}M`
                    : creator.followerCount >= 1000
                      ? `${(creator.followerCount / 1000).toFixed(1)}K`
                      : creator.followerCount}
                </Text>
              </View>

              <View style={styles.metaItem}>
                <BookOpen size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodySmall'
                  style={{
                    color: theme.colors.onSurfaceVariant,
                    marginLeft: 4,
                  }}
                >
                  {creator.recipeCount} recipes
                </Text>
              </View>
            </View>

            {creator.specialties && creator.specialties.length > 0 && (
              <View style={styles.specialtiesContainer}>
                {creator.specialties
                  .slice(0, 3)
                  .map((specialty: string, index: number) => (
                    <View
                      key={index}
                      style={[
                        styles.specialtyChip,
                        { backgroundColor: '#f3f4f6' },
                      ]}
                    >
                      <Text
                        style={{
                          color: '#374151',
                          fontSize: 10,
                          fontWeight: '500',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                        }}
                      >
                        {specialty}
                      </Text>
                    </View>
                  ))}
              </View>
            )}
          </View>
        </View>
      </Card.Content>
    </Card>
  )
);

CreatorCardItem.displayName = 'CreatorCardItem';

// Memoized RecipeCard wrapper to prevent unnecessary re-renders
const RecipeCardItem = memo(
  ({
    recipe,
    onRecipePress,
    refetchInteractions,
  }: {
    recipe: Recipe;
    onRecipePress: (recipe: Recipe) => void;
    refetchInteractions: () => void;
  }) => {
    const { isLiked, isBookmarked, toggleLike, toggleBookmark } =
      useRecipePrefsItem(recipe.id);

    const { like, unlike, bookmark, unbookmark } = useRecipeInteractions(
      recipe.id
    );

    const handleLike = useCallback(async () => {
      const newState = toggleLike();
      if (newState) {
        like();
      } else {
        unlike();
      }
      refetchInteractions();
    }, [toggleLike, like, unlike, refetchInteractions]);

    const handleBookmark = useCallback(async () => {
      const newState = toggleBookmark();
      if (newState) {
        bookmark();
      } else {
        unbookmark();
      }
      refetchInteractions();
    }, [toggleBookmark, bookmark, unbookmark, refetchInteractions]);

    return (
      <RecipeCard
        recipe={recipe}
        onPress={onRecipePress}
        onLike={handleLike}
        onBookmark={handleBookmark}
        liked={isLiked}
        bookmarked={isBookmarked}
        variant='default'
        testID={`recipe-${recipe.id}`}
      />
    );
  }
);

RecipeCardItem.displayName = 'RecipeCardItem';

export function FavoritesScreen() {
  const theme = useTheme();
  const navigation = useNavigation<FavoritesScreenNavigationProp>();
  const dispatch = useAppDispatch();
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'bookmarked' | 'liked' | 'creators'
  >('bookmarked');
  const [hasLoadedTabs, setHasLoadedTabs] = useState<{
    bookmarked: boolean;
    liked: boolean;
    creators: boolean;
  }>({
    bookmarked: false,
    liked: false,
    creators: false,
  });

  const { userRecipes, isLoading } = useAppSelector(state => state.recipes);

  const { creators, loading: creatorsLoading } = useAppSelector(
    state => state.creators
  );

  // Filter creators to show only followed ones
  const favoriteCreators = useMemo(
    () => creators.filter(creator => creator.isFollowing),
    [creators]
  );

  // Get recipe IDs for bulk interaction fetch
  const recipeIds = useMemo(() => userRecipes.map(r => r.id), [userRecipes]);

  // Fetch bulk interaction status from Redis (with DB fallback)
  const { data: bulkInteractions, refetch: refetchInteractions } =
    useBulkRecipeInteractions(recipeIds, recipeIds.length > 0);

  // Filter recipes based on active tab and interaction status
  const filteredRecipes = useMemo(() => {
    if (!bulkInteractions || activeTab === 'creators') {
      return userRecipes;
    }

    return userRecipes.filter(recipe => {
      const interaction = bulkInteractions[recipe.id];
      if (!interaction) return false;

      if (activeTab === 'bookmarked') {
        return interaction.is_bookmarked;
      } else if (activeTab === 'liked') {
        return interaction.is_liked;
      }
      return false;
    });
  }, [userRecipes, bulkInteractions, activeTab]);

  // Sync bulk interactions with local store
  const setLike = useRecipePrefsStore(s => s.setLike);
  const setBookmark = useRecipePrefsStore(s => s.setBookmark);

  useEffect(() => {
    if (bulkInteractions) {
      // Sync Redis/DB data with local Zustand store
      Object.entries(bulkInteractions).forEach(([recipeId, status]) => {
        setLike(recipeId, status.is_liked);
        setBookmark(recipeId, status.is_bookmarked);
      });
    }
  }, [bulkInteractions, setLike, setBookmark]);

  useEffect(() => {
    // Only load if we haven't loaded this tab before
    if (!hasLoadedTabs[activeTab]) {
      loadFavorites();
    }
  }, [activeTab]);

  const loadFavorites = async () => {
    try {
      if (activeTab === 'creators') {
        // Fetch all creators to get the followed ones
        await dispatch(fetchCreators({ page: 1 })).unwrap();
      } else {
        await dispatch(
          fetchUserRecipes({ type: activeTab, page: 1, limit: 20 })
        ).unwrap();
      }

      // Mark this tab as loaded
      setHasLoadedTabs(prev => ({ ...prev, [activeTab]: true }));
    } catch (error: any) {
      logger.error('Failed to load favorites:', error);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadFavorites();
      if (activeTab !== 'creators' && recipeIds.length > 0) {
        await refetchInteractions();
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleRecipePress = (recipe: Recipe) => {
    navigation.getParent()?.navigate('recipes', {
      screen: 'recipeDetail',
      params: { recipeId: recipe.id },
    });
  };

  const handleUnfollowCreator = async (creatorId: string) => {
    try {
      await dispatch(unfollowCreator(creatorId)).unwrap();
    } catch (error: any) {
      logger.error('Failed to unfollow creator:', error);
    }
  };

  // Determine if we should show the full-screen loading state
  // Only show it on initial load of a tab that hasn't been loaded yet
  const shouldShowFullScreenLoading = () => {
    if (refreshing) return false; // Don't show full screen when refreshing

    const isTabLoaded = hasLoadedTabs[activeTab];
    if (isTabLoaded) return false; // Tab already loaded, show cached data

    // Show loading only if we're actually loading and have no data
    if (activeTab === 'creators') {
      return creatorsLoading && favoriteCreators.length === 0;
    } else {
      return isLoading && userRecipes.length === 0;
    }
  };

  const renderRecipeCard = useCallback(
    ({ item: recipe }: { item: Recipe }) => (
      <RecipeCardItem
        recipe={recipe}
        onRecipePress={handleRecipePress}
        refetchInteractions={refetchInteractions}
      />
    ),
    [handleRecipePress, refetchInteractions]
  );

  const renderCreatorCard = useCallback(
    ({ item: creator }: { item: any }) => {
      const handlePress = () => {
        navigation.getParent()?.navigate('creators', {
          screen: 'creatorProfile',
          params: { creatorId: creator.id },
        });
      };

      const handleUnfollow = () => {
        handleUnfollowCreator(creator.id);
      };

      return (
        <CreatorCardItem
          creator={creator}
          onPress={handlePress}
          onUnfollow={handleUnfollow}
          theme={theme}
        />
      );
    },
    [theme, navigation, handleUnfollowCreator]
  );

  const renderEmptyState = () => {
    if (activeTab === 'creators') {
      return (
        <View style={styles.emptyState}>
          <Heart size={64} color={theme.colors.onSurfaceVariant} />
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurfaceVariant, marginTop: 16 }}
          >
            No favorite creators yet
          </Text>
          <Text
            variant='bodyMedium'
            style={{
              color: theme.colors.onSurfaceVariant,
              textAlign: 'center',
              marginTop: 8,
            }}
          >
            Start following coffee creators to see them here
          </Text>
          <View style={styles.emptyStateButtons}>
            <Button
              mode='contained'
              icon={Coffee}
              onPress={() => navigation.getParent()?.navigate('creators')}
              style={styles.emptyStateButton}
            >
              Browse Creators
            </Button>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.emptyState}>
        <Heart size={64} color={theme.colors.onSurfaceVariant} />
        <Text
          variant='titleMedium'
          style={{ color: theme.colors.onSurfaceVariant, marginTop: 16 }}
        >
          No {activeTab === 'bookmarked' ? 'bookmarked' : 'liked'} recipes yet
        </Text>
        <Text
          variant='bodyMedium'
          style={{
            color: theme.colors.onSurfaceVariant,
            textAlign: 'center',
            marginTop: 8,
          }}
        >
          Start exploring recipes and save your favorites to see them here
        </Text>
        <View style={styles.emptyStateButtons}>
          <Button
            mode='contained'
            icon={Coffee}
            onPress={() => navigation.getParent()?.navigate('recipes')}
            style={styles.emptyStateButton}
          >
            Browse Recipes
          </Button>
        </View>
      </View>
    );
  };

  const isCurrentlyLoading = shouldShowFullScreenLoading();

  if (isCurrentlyLoading) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size='large' color={theme.colors.primary} />
          <Text style={{ color: theme.colors.onSurfaceVariant, marginTop: 16 }}>
            Loading your favorites...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Header */}
      <View style={styles.header}>
        <Text
          variant='headlineMedium'
          style={{ color: theme.colors.onBackground }}
        >
          My Favorites
        </Text>
        <Text
          variant='bodyLarge'
          style={{ color: theme.colors.onSurfaceVariant }}
        >
          {activeTab === 'creators'
            ? 'Your favorite coffee creators'
            : 'Your saved coffee recipes'}
        </Text>
      </View>

      {/* Tab Selector */}
      <View style={styles.tabScrollView}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabContainer}
        >
          <Button
            mode={activeTab === 'bookmarked' ? 'contained' : 'outlined'}
            onPress={() => setActiveTab('bookmarked')}
            style={styles.tabButton}
            contentStyle={styles.tabButtonContent}
            compact
            labelStyle={{
              color:
                activeTab === 'bookmarked'
                  ? theme.colors.onPrimary
                  : theme.colors.primary,
            }}
          >
            Bookmarked
          </Button>
          <Button
            mode={activeTab === 'liked' ? 'contained' : 'outlined'}
            onPress={() => setActiveTab('liked')}
            style={styles.tabButton}
            contentStyle={styles.tabButtonContent}
            compact
            labelStyle={{
              color:
                activeTab === 'liked'
                  ? theme.colors.onPrimary
                  : theme.colors.primary,
            }}
          >
            Liked
          </Button>
          <Button
            mode={activeTab === 'creators' ? 'contained' : 'outlined'}
            onPress={() => setActiveTab('creators')}
            style={styles.tabButton}
            contentStyle={styles.tabButtonContent}
            compact
            labelStyle={{
              color:
                activeTab === 'creators'
                  ? theme.colors.onPrimary
                  : theme.colors.primary,
            }}
          >
            Creators
          </Button>
        </ScrollView>
      </View>

      {/* Subtle loading indicator below tabs */}
      {!hasLoadedTabs[activeTab] &&
        (activeTab === 'creators' ? creatorsLoading : isLoading) && (
          <View style={styles.tabLoadingIndicator}>
            <ActivityIndicator size='small' color={theme.colors.primary} />
            <Text
              variant='bodySmall'
              style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}
            >
              Loading...
            </Text>
          </View>
        )}

      {/* Content List */}
      {activeTab === 'creators' ? (
        <FlatList
          data={favoriteCreators}
          renderItem={renderCreatorCard}
          keyExtractor={item => item.id}
          contentContainerStyle={[
            styles.listContainer,
            favoriteCreators.length === 0 && { flex: 1 },
          ]}
          showsVerticalScrollIndicator={false}
          removeClippedSubviews={true}
          maxToRenderPerBatch={10}
          updateCellsBatchingPeriod={50}
          initialNumToRender={10}
          windowSize={10}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListEmptyComponent={renderEmptyState}
        />
      ) : (
        <FlatList
          data={filteredRecipes}
          renderItem={renderRecipeCard}
          keyExtractor={item => item.id}
          contentContainerStyle={[
            styles.listContainer,
            filteredRecipes.length === 0 && { flex: 1 },
          ]}
          showsVerticalScrollIndicator={false}
          removeClippedSubviews={true}
          maxToRenderPerBatch={10}
          updateCellsBatchingPeriod={50}
          initialNumToRender={10}
          windowSize={10}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListEmptyComponent={renderEmptyState}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  tabScrollView: {
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  tabContainer: {
    gap: 8,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  tabButton: {
    minWidth: 110,
    marginHorizontal: 0,
    height: 40,
  },
  tabButtonContent: {
    height: 40,
    paddingHorizontal: 12,
  },
  tabLoadingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  listContainer: {
    paddingHorizontal: 16,
    paddingBottom: 20,
    flexGrow: 1,
  },
  separator: {
    height: 0,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    paddingVertical: 64,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Creator card styles
  creatorCard: {
    marginBottom: 16,
  },
  creatorHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  avatarContainer: {
    position: 'relative',
    marginRight: 16,
  },
  avatar: {
    borderRadius: 30,
  },
  verifiedBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    borderRadius: 10,
    padding: 2,
  },
  creatorInfo: {
    flex: 1,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  creatorMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  specialtiesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  specialtyChip: {
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  removeButtonContainer: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
  },
  removeButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    margin: 0,
  },
  emptyStateButtons: {
    flexDirection: 'row',
    marginTop: 16,
  },
  emptyStateButton: {
    flex: 1,
  },
});
